/**
 * Hybrid System Integration constants and educational presets.
 * Defines the first-draft component library, scenarios, control strategies,
 * colors, and defaults used by the interactive hybrid heating system tool.
 * @module HybridSystemIntegration/constants
 */

/** @enum {string} Supported component type identifiers. */
export const COMPONENT_TYPES = {
  AIR_SOURCE_HEAT_PUMP: 'airSourceHeatPump',
  GROUND_SOURCE_HEAT_PUMP: 'groundSourceHeatPump',
  GAS_BOILER: 'gasBoiler',
  SOLAR_PV: 'solarPv',
  THERMAL_STORAGE: 'thermalStorage',
  BUILDING_LOAD: 'buildingLoad',
  SMART_CONTROLLER: 'smartController',
};

/** @enum {string} Visual energy flow categories. */
export const ENERGY_TYPES = {
  ELECTRIC: 'electric',
  THERMAL: 'thermal',
  AMBIENT: 'ambient',
  FOSSIL: 'fossil',
  CONTROL: 'control',
};

/** Canvas dimensions used by the SVG animation layer. */
export const CANVAS_SIZE = {
  width: 920,
  height: 560,
};

/** Canvas node footprint used for positioning and hit areas. */
export const CANVAS_NODE_SIZE = {
  width: 108,
  height: 88,
};

/**
 * Control strategy metadata for load dispatch education.
 * Translation keys are resolved in the UI so constants remain framework-neutral.
 */
export const CONTROL_STRATEGIES = {
  heatPumpPriority: {
    key: 'heatPumpPriority',
    translationKey: 'hybridSystemTool.strategies.heatPumpPriority',
    color: '#55769b',
  },
  bivalentParallel: {
    key: 'bivalentParallel',
    translationKey: 'hybridSystemTool.strategies.bivalentParallel',
    color: '#5f8f88',
  },
  bivalentAlternative: {
    key: 'bivalentAlternative',
    translationKey: 'hybridSystemTool.strategies.bivalentAlternative',
    color: '#75669c',
  },
  costOptimized: {
    key: 'costOptimized',
    translationKey: 'hybridSystemTool.strategies.costOptimized',
    color: '#a67744',
  },
  co2Optimized: {
    key: 'co2Optimized',
    translationKey: 'hybridSystemTool.strategies.co2Optimized',
    color: '#5f8660',
  },
};

/** Building insulation presets used by the simplified load model. */
export const INSULATION_PRESETS = {
  poor: {
    key: 'poor',
    translationKey: 'hybridSystemTool.insulation.poor',
    heatLossWPerM2K: 2.2,
  },
  retrofit: {
    key: 'retrofit',
    translationKey: 'hybridSystemTool.insulation.retrofit',
    heatLossWPerM2K: 1.25,
  },
  passive: {
    key: 'passive',
    translationKey: 'hybridSystemTool.insulation.passive',
    heatLossWPerM2K: 0.55,
  },
};

/** Scenario cards for quick educational demonstrations. */
export const SCENARIOS = {
  balanced: {
    key: 'balanced',
    translationKey: 'hybridSystemTool.scenarios.balanced',
    outdoorTemperature: 3,
    electricityPrice: 0.32,
    gasPrice: 0.11,
    electricityCarbonIntensity: 0.34,
    solarIrradiance: 0.55,
    strategy: 'heatPumpPriority',
    storageLevel: 58,
  },
  coldSnap: {
    key: 'coldSnap',
    translationKey: 'hybridSystemTool.scenarios.coldSnap',
    outdoorTemperature: -9,
    electricityPrice: 0.35,
    gasPrice: 0.12,
    electricityCarbonIntensity: 0.42,
    solarIrradiance: 0.18,
    strategy: 'bivalentParallel',
    storageLevel: 35,
  },
  solarNoon: {
    key: 'solarNoon',
    translationKey: 'hybridSystemTool.scenarios.solarNoon',
    outdoorTemperature: 8,
    electricityPrice: 0.22,
    gasPrice: 0.11,
    electricityCarbonIntensity: 0.18,
    solarIrradiance: 0.95,
    strategy: 'co2Optimized',
    storageLevel: 78,
  },
  priceSpike: {
    key: 'priceSpike',
    translationKey: 'hybridSystemTool.scenarios.priceSpike',
    outdoorTemperature: 1,
    electricityPrice: 0.58,
    gasPrice: 0.1,
    electricityCarbonIntensity: 0.39,
    solarIrradiance: 0.3,
    strategy: 'costOptimized',
    storageLevel: 44,
  },
};

/** Default system-level sliders and economic assumptions. */
export const DEFAULT_SYSTEM_SETTINGS = {
  outdoorTemperature: 3,
  indoorTemperature: 21,
  floorArea: 160,
  insulationLevel: 'retrofit',
  electricityPrice: 0.32,
  gasPrice: 0.11,
  electricityCarbonIntensity: 0.34,
  gasCarbonIntensity: 0.202,
  solarIrradiance: 0.55,
  hourOfDay: 14,
};

/** Component palette and default specification values. */
export const COMPONENT_LIBRARY = {
  [COMPONENT_TYPES.AIR_SOURCE_HEAT_PUMP]: {
    type: COMPONENT_TYPES.AIR_SOURCE_HEAT_PUMP,
    translationKey: 'hybridSystemTool.components.airSourceHeatPump',
    categoryTranslationKey: 'hybridSystemTool.categories.primarySource',
    icon: '🌬️',
      color: '#5c7896',
    energyType: ENERGY_TYPES.AMBIENT,
    defaultSpecs: {
      ratedCapacityKw: 10,
      supplyTemperature: 45,
      bivalenceTemperature: -4,
    },
  },
  [COMPONENT_TYPES.GROUND_SOURCE_HEAT_PUMP]: {
    type: COMPONENT_TYPES.GROUND_SOURCE_HEAT_PUMP,
    translationKey: 'hybridSystemTool.components.groundSourceHeatPump',
    categoryTranslationKey: 'hybridSystemTool.categories.primarySource',
    icon: '🌍',
      color: '#6b8e88',
    energyType: ENERGY_TYPES.AMBIENT,
    defaultSpecs: {
      ratedCapacityKw: 12,
      supplyTemperature: 42,
      bivalenceTemperature: -8,
    },
  },
  [COMPONENT_TYPES.GAS_BOILER]: {
    type: COMPONENT_TYPES.GAS_BOILER,
    translationKey: 'hybridSystemTool.components.gasBoiler',
    categoryTranslationKey: 'hybridSystemTool.categories.auxiliarySource',
    icon: '🔥',
      color: '#a8674f',
    energyType: ENERGY_TYPES.FOSSIL,
    defaultSpecs: {
      ratedCapacityKw: 18,
      efficiency: 0.92,
    },
  },
  [COMPONENT_TYPES.SOLAR_PV]: {
    type: COMPONENT_TYPES.SOLAR_PV,
    translationKey: 'hybridSystemTool.components.solarPv',
    categoryTranslationKey: 'hybridSystemTool.categories.renewableSource',
    icon: '☀️',
      color: '#b08f4b',
    energyType: ENERGY_TYPES.ELECTRIC,
    defaultSpecs: {
      capacityKw: 6,
    },
  },
  [COMPONENT_TYPES.THERMAL_STORAGE]: {
    type: COMPONENT_TYPES.THERMAL_STORAGE,
    translationKey: 'hybridSystemTool.components.thermalStorage',
    categoryTranslationKey: 'hybridSystemTool.categories.storage',
    icon: '🛢️',
      color: '#8b6f97',
    energyType: ENERGY_TYPES.THERMAL,
    defaultSpecs: {
      capacityKwh: 18,
      maxDischargeKw: 4,
    },
  },
  [COMPONENT_TYPES.BUILDING_LOAD]: {
    type: COMPONENT_TYPES.BUILDING_LOAD,
    translationKey: 'hybridSystemTool.components.buildingLoad',
    categoryTranslationKey: 'hybridSystemTool.categories.demand',
    icon: '🏢',
      color: '#5a6f8f',
    energyType: ENERGY_TYPES.THERMAL,
    defaultSpecs: {},
  },
  [COMPONENT_TYPES.SMART_CONTROLLER]: {
    type: COMPONENT_TYPES.SMART_CONTROLLER,
    translationKey: 'hybridSystemTool.components.smartController',
    categoryTranslationKey: 'hybridSystemTool.categories.control',
    icon: '🧠',
      color: '#6d6a93',
    energyType: ENERGY_TYPES.CONTROL,
    defaultSpecs: {},
  },
};

/** Starting layout for the first-draft hybrid system builder canvas. */
export const DEFAULT_NODES = [
  {
    id: 'node-ashp',
    type: COMPONENT_TYPES.AIR_SOURCE_HEAT_PUMP,
    x: 100,
    y: 190,
    specs: { ...COMPONENT_LIBRARY[COMPONENT_TYPES.AIR_SOURCE_HEAT_PUMP].defaultSpecs },
  },
  {
    id: 'node-boiler',
    type: COMPONENT_TYPES.GAS_BOILER,
    x: 120,
    y: 370,
    specs: { ...COMPONENT_LIBRARY[COMPONENT_TYPES.GAS_BOILER].defaultSpecs },
  },
  {
    id: 'node-pv',
    type: COMPONENT_TYPES.SOLAR_PV,
    x: 370,
    y: 50,
    specs: { ...COMPONENT_LIBRARY[COMPONENT_TYPES.SOLAR_PV].defaultSpecs },
  },
  {
    id: 'node-storage',
    type: COMPONENT_TYPES.THERMAL_STORAGE,
    x: 605,
    y: 375,
    specs: { ...COMPONENT_LIBRARY[COMPONENT_TYPES.THERMAL_STORAGE].defaultSpecs },
  },
  {
    id: 'node-controller',
    type: COMPONENT_TYPES.SMART_CONTROLLER,
    x: 395,
    y: 255,
    specs: { ...COMPONENT_LIBRARY[COMPONENT_TYPES.SMART_CONTROLLER].defaultSpecs },
  },
  {
    id: 'node-load',
    type: COMPONENT_TYPES.BUILDING_LOAD,
    x: 675,
    y: 175,
    specs: { ...COMPONENT_LIBRARY[COMPONENT_TYPES.BUILDING_LOAD].defaultSpecs },
  },
];

/** Text glossary terms displayed in the educational layer. */
export const GLOSSARY_TERMS = [
  {
    key: 'cop',
    titleKey: 'hybridSystemTool.glossary.cop.title',
    bodyKey: 'hybridSystemTool.glossary.cop.body',
  },
  {
    key: 'bivalence',
    titleKey: 'hybridSystemTool.glossary.bivalence.title',
    bodyKey: 'hybridSystemTool.glossary.bivalence.body',
  },
  {
    key: 'dispatch',
    titleKey: 'hybridSystemTool.glossary.dispatch.title',
    bodyKey: 'hybridSystemTool.glossary.dispatch.body',
  },
];
